<script setup lang="ts">
import type { Relation } from "~/types/article"
import type { FasitSidenote } from "~/models/FasitSidenote"

export type SidenoteData = Relation | FasitSidenote

defineProps<{ activeNote?: SidenoteData }>()

defineEmits(["close"])

const closeButton = useTemplateRef("closeButton")
</script>

<template>
  <transition
    name="sidenote"
    mode="out-in"
    class="transition-transform duration-300"
    enter-active-class="translate-x-0 ease-out"
    leave-active-class="translate-x-0 ease-in"
    enter-from-class="translate-x-full"
    leave-to-class="translate-x-full"
    @after-enter="closeButton?.$el?.focus()"
  >
    <div
      v-if="activeNote"
      id="activeNote"
      style="box-shadow: 0 0 8px rgba(0, 0, 0, 0.25)"
      class="sticky z-30 col-start-1 row-start-1 grid h-full grid-rows-[0,1fr] items-center place-self-end overflow-auto bg-grey-20 !font-national2 md:top-0 md:w-[var(--sidebar-width)] <md:fixed <md:inset-0 <md:overflow-auto <md:rounded"
      :class="{
        '!bg-yellow-20':
          activeNote.identifier === 'explainer' ||
          activeNote.identifier === 'what_is',
        '!bg-green-lightest':
          activeNote.identifier === 'solution',
      }"
    >
      <UCloseSidenoteButton ref="closeButton" @click="$emit('close')" />
      <USidenotePerson
        v-if="activeNote.identifier === 'person'"
        :note="activeNote"
      />
      <USidenoteWhatIs
        v-else-if="activeNote.identifier === 'what_is'"
        :note="activeNote"
      />
      <USidenoteTerm
        v-else-if="activeNote.identifier === 'term'"
        :note="activeNote"
      />
      <USidenoteSource
        v-else-if="activeNote.identifier === 'source'"
        :note="activeNote"
      />
      <USidenoteLegalSource
        v-else-if="activeNote.identifier === 'legal_source'"
        :note="activeNote"
      />
      <USidenoteSolution
        v-else-if="activeNote.identifier === 'solution'"
        :note="activeNote"
      />
      <USidenoteDefault v-else :note="activeNote" />
    </div>
  </transition>
</template>

<style lang="postcss" scoped>
/* Numbers inside fractions in math elements looks smaller than their applied font. This style ensures the numbers
inside fractions looks similar as the numbers outside fractions in the sidenote, so that they are more readable.
Fractions inside superscript elements should not be enlarged.
 */
:deep(math mfrac):not(msup > mfrac) {
  font-size: 28px;
}

/* Styles specifically for fractions Safari */
@supports (-webkit-backdrop-filter: blur(10px)) {
  :deep(math mfrac):not(msup > mfrac) {
    font-size: revert;
  }
}
</style>
